/**
 * Europe + Africa catalogue — the pick-list behind Admin → Currencies and
 * Admin → Corridors.
 *
 * This file is a CATALOGUE, not a platform state. Nothing here is "on":
 * `public.countries`, `public.corridor_pairs` and `public.platform_currencies`
 * remain the only authorities for what EuroCollis actually offers. The
 * catalogue exists so an admin can pick a real country or currency from a
 * complete list instead of typing codes, and so every insert carries correct
 * ISO metadata (calling code, currency, flag).
 *
 * Currency *rendering* metadata still lives in the single money authority
 * (`@shared/money/presentation`); this module only maps countries → currencies
 * and tags each one with its corridor side.
 */

import { CURRENCY_REGISTRY, type CurrencyDef } from "@/lib/money";

export type CorridorRegion = "europe" | "africa";

export interface CatalogueCountry {
  iso: string;
  name: string;
  callingCode: string;
  currency: string;
  region: CorridorRegion;
}

/** Flag emoji from an ISO-3166 alpha-2 code (regional indicator letters). */
export function isoToFlag(iso: string): string {
  const code = String(iso || "").trim().toUpperCase();
  if (code.length !== 2) return "🏳️";
  return String.fromCodePoint(...[...code].map((c) => 0x1f1a5 + c.charCodeAt(0)));
}

type Row = [iso: string, name: string, callingCode: string, currency: string];

const EUROPE: Row[] = [
  ["AL", "Albania", "+355", "ALL"],
  ["AD", "Andorra", "+376", "EUR"],
  ["AT", "Austria", "+43", "EUR"],
  ["BY", "Belarus", "+375", "BYN"],
  ["BE", "Belgium", "+32", "EUR"],
  ["BA", "Bosnia and Herzegovina", "+387", "BAM"],
  ["BG", "Bulgaria", "+359", "BGN"],
  ["HR", "Croatia", "+385", "EUR"],
  ["CY", "Cyprus", "+357", "EUR"],
  ["CZ", "Czechia", "+420", "CZK"],
  ["DK", "Denmark", "+45", "DKK"],
  ["EE", "Estonia", "+372", "EUR"],
  ["FI", "Finland", "+358", "EUR"],
  ["FR", "France", "+33", "EUR"],
  ["DE", "Germany", "+49", "EUR"],
  ["GR", "Greece", "+30", "EUR"],
  ["HU", "Hungary", "+36", "HUF"],
  ["IS", "Iceland", "+354", "ISK"],
  ["IE", "Ireland", "+353", "EUR"],
  ["IT", "Italy", "+39", "EUR"],
  ["XK", "Kosovo", "+383", "EUR"],
  ["LV", "Latvia", "+371", "EUR"],
  ["LI", "Liechtenstein", "+423", "CHF"],
  ["LT", "Lithuania", "+370", "EUR"],
  ["LU", "Luxembourg", "+352", "EUR"],
  ["MT", "Malta", "+356", "EUR"],
  ["MD", "Moldova", "+373", "MDL"],
  ["MC", "Monaco", "+377", "EUR"],
  ["ME", "Montenegro", "+382", "EUR"],
  ["NL", "Netherlands", "+31", "EUR"],
  ["MK", "North Macedonia", "+389", "MKD"],
  ["NO", "Norway", "+47", "NOK"],
  ["PL", "Poland", "+48", "PLN"],
  ["PT", "Portugal", "+351", "EUR"],
  ["RO", "Romania", "+40", "RON"],
  ["SM", "San Marino", "+378", "EUR"],
  ["RS", "Serbia", "+381", "RSD"],
  ["SK", "Slovakia", "+421", "EUR"],
  ["SI", "Slovenia", "+386", "EUR"],
  ["ES", "Spain", "+34", "EUR"],
  ["SE", "Sweden", "+46", "SEK"],
  ["CH", "Switzerland", "+41", "CHF"],
  ["TR", "Türkiye", "+90", "TRY"],
  ["UA", "Ukraine", "+380", "UAH"],
  ["GB", "United Kingdom", "+44", "GBP"],
];

const AFRICA: Row[] = [
  ["DZ", "Algeria", "+213", "DZD"],
  ["AO", "Angola", "+244", "AOA"],
  ["BJ", "Benin", "+229", "XOF"],
  ["BW", "Botswana", "+267", "BWP"],
  ["BF", "Burkina Faso", "+226", "XOF"],
  ["BI", "Burundi", "+257", "BIF"],
  ["CV", "Cabo Verde", "+238", "CVE"],
  ["CM", "Cameroon", "+237", "XAF"],
  ["CF", "Central African Republic", "+236", "XAF"],
  ["TD", "Chad", "+235", "XAF"],
  ["KM", "Comoros", "+269", "KMF"],
  ["CG", "Congo", "+242", "XAF"],
  ["CD", "DR Congo", "+243", "CDF"],
  ["CI", "Côte d'Ivoire", "+225", "XOF"],
  ["DJ", "Djibouti", "+253", "DJF"],
  ["EG", "Egypt", "+20", "EGP"],
  ["GQ", "Equatorial Guinea", "+240", "XAF"],
  ["ER", "Eritrea", "+291", "ERN"],
  ["SZ", "Eswatini", "+268", "SZL"],
  ["ET", "Ethiopia", "+251", "ETB"],
  ["GA", "Gabon", "+241", "XAF"],
  ["GM", "Gambia", "+220", "GMD"],
  ["GH", "Ghana", "+233", "GHS"],
  ["GN", "Guinea", "+224", "GNF"],
  ["GW", "Guinea-Bissau", "+245", "XOF"],
  ["KE", "Kenya", "+254", "KES"],
  ["LS", "Lesotho", "+266", "LSL"],
  ["LR", "Liberia", "+231", "LRD"],
  ["LY", "Libya", "+218", "LYD"],
  ["MG", "Madagascar", "+261", "MGA"],
  ["MW", "Malawi", "+265", "MWK"],
  ["ML", "Mali", "+223", "XOF"],
  ["MR", "Mauritania", "+222", "MRU"],
  ["MU", "Mauritius", "+230", "MUR"],
  ["MA", "Morocco", "+212", "MAD"],
  ["MZ", "Mozambique", "+258", "MZN"],
  ["NA", "Namibia", "+264", "NAD"],
  ["NE", "Niger", "+227", "XOF"],
  ["NG", "Nigeria", "+234", "NGN"],
  ["RW", "Rwanda", "+250", "RWF"],
  ["ST", "São Tomé and Príncipe", "+239", "STN"],
  ["SN", "Senegal", "+221", "XOF"],
  ["SC", "Seychelles", "+248", "SCR"],
  ["SL", "Sierra Leone", "+232", "SLE"],
  ["SO", "Somalia", "+252", "SOS"],
  ["ZA", "South Africa", "+27", "ZAR"],
  ["SS", "South Sudan", "+211", "SSP"],
  ["SD", "Sudan", "+249", "SDG"],
  ["TZ", "Tanzania", "+255", "TZS"],
  ["TG", "Togo", "+228", "XOF"],
  ["TN", "Tunisia", "+216", "TND"],
  ["UG", "Uganda", "+256", "UGX"],
  ["ZM", "Zambia", "+260", "ZMW"],
  ["ZW", "Zimbabwe", "+263", "ZWG"],
];

const toCountry = (region: CorridorRegion) => ([iso, name, callingCode, currency]: Row): CatalogueCountry => ({
  iso,
  name,
  callingCode,
  currency,
  region,
});

export const CATALOGUE_COUNTRIES: CatalogueCountry[] = [
  ...EUROPE.map(toCountry("europe")),
  ...AFRICA.map(toCountry("africa")),
].sort((a, b) => a.name.localeCompare(b.name));

export function catalogueCountry(iso?: string | null): CatalogueCountry | undefined {
  const code = String(iso || "").trim().toUpperCase();
  return CATALOGUE_COUNTRIES.find((c) => c.iso === code);
}

export interface CatalogueCurrency extends CurrencyDef {
  /** Where the currency is spent. `global` = used on both sides or worldwide. */
  region: CorridorRegion | "global";
  /** Countries in the catalogue that use it, by ISO code. */
  countries: string[];
  /** Human list of the biggest users, for the picker subtitle. */
  usedBy: string;
}

function buildCurrencies(): CatalogueCurrency[] {
  const byCode = new Map<string, { europe: number; africa: number; countries: CatalogueCountry[] }>();
  for (const c of CATALOGUE_COUNTRIES) {
    const entry = byCode.get(c.currency) ?? { europe: 0, africa: 0, countries: [] };
    entry[c.region] += 1;
    entry.countries.push(c);
    byCode.set(c.currency, entry);
  }

  const out: CatalogueCurrency[] = [];
  for (const [code, entry] of byCode) {
    const def = (CURRENCY_REGISTRY as Record<string, CurrencyDef>)[code];
    if (!def) continue; // no rendering metadata yet → not offerable
    out.push({
      ...def,
      region: entry.europe && entry.africa ? "global" : entry.africa ? "africa" : "europe",
      countries: entry.countries.map((c) => c.iso),
      usedBy: entry.countries
        .slice(0, 4)
        .map((c) => c.name)
        .join(", ") + (entry.countries.length > 4 ? ` +${entry.countries.length - 4}` : ""),
    });
  }
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

/** Every currency spendable in Europe or Africa that the money authority can render. */
export const CATALOGUE_CURRENCIES: CatalogueCurrency[] = buildCurrencies();

export const REGION_LABEL: Record<CorridorRegion | "global", string> = {
  europe: "Europe",
  africa: "Africa",
  global: "Europe & Africa",
};
